import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, UserCog, Eye, Save, Search, X, Plus, MapPin, Bell } from "lucide-react";

const MODE_FULL = "full";
const MODE_PARTIAL = "partial";

function AgentCard({ agent, codAgenteStats, giriOptions, authAxios, onSave, onToggleGeo, onTogglePush }) {
  const geoOn = !!agent.geo_tracking;
  const pushOn = !!agent.push_enabled;
  const initial = agent.client_access || { mode: MODE_FULL, cod_agente_codes: [], extra_codice_cli: [], allowed_giri: [] };
  const [mode, setMode] = useState((initial.mode || MODE_FULL).toLowerCase());
  const [codes, setCodes] = useState(new Set((initial.cod_agente_codes || []).map(Number)));
  // extras è un array di {codice_cli, ragione_sociale}
  const [extras, setExtras] = useState([]);
  const [extrasLoaded, setExtrasLoaded] = useState(false);
  // Giri whitelist: Set di stringhe "cod_agente|cod_giorno"
  const [allowedGiri, setAllowedGiri] = useState(
    new Set((initial.allowed_giri || []).map((g) => `${g.cod_agente}|${g.cod_giorno}`))
  );
  const [saving, setSaving] = useState(false);
  const [visibleCount, setVisibleCount] = useState(agent.visible_count ?? 0);
  // Autocomplete
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchTimer = useRef(null);

  // Risolve i codici già salvati nei nominativi (al primo render).
  useEffect(() => {
    if (extrasLoaded) return;
    const saved = (initial.extra_codice_cli || []).map((c) => String(c).toUpperCase().trim()).filter(Boolean);
    if (saved.length === 0) {
      setExtras([]);
      setExtrasLoaded(true);
      return;
    }
    authAxios
      .post("/admin/clients-lookup/by-codes", { codes: saved })
      .then((res) => {
        const items = res.data?.items || [];
        const byCode = Object.fromEntries(items.map((i) => [i.codice_cli, i]));
        // Mantieni l'ordine dei codici salvati e mostra i nomi noti, oppure il codice se non trovato
        setExtras(
          saved.map((c) => byCode[c] || { codice_cli: c, ragione_sociale: "(non trovato)" })
        );
      })
      .catch(() => setExtras(saved.map((c) => ({ codice_cli: c, ragione_sociale: "" }))))
      .finally(() => setExtrasLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await authAxios.get("/admin/clients-lookup", {
          params: { q: query.trim(), limit: 15 },
        });
        setResults(res.data?.items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [query, authAxios]);

  const toggleCode = (c) => {
    setCodes((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const addExtra = (item) => {
    setExtras((prev) => {
      if (prev.some((e) => e.codice_cli === item.codice_cli)) return prev;
      return [...prev, item];
    });
    setQuery("");
    setResults([]);
    setDropdownOpen(false);
  };

  const removeExtra = (code) => {
    setExtras((prev) => prev.filter((e) => e.codice_cli !== code));
  };

  const toggleGiro = (giroKey) => {
    setAllowedGiri((prev) => {
      const next = new Set(prev);
      if (next.has(giroKey)) next.delete(giroKey);
      else next.add(giroKey);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        mode,
        cod_agente_codes: Array.from(codes),
        extra_codice_cli: extras.map((e) => e.codice_cli),
        allowed_giri: Array.from(allowedGiri).map((k) => {
          const [ca, cg] = k.split("|");
          return { cod_agente: ca, cod_giorno: cg };
        }),
      };
      const res = await onSave(agent.id, payload);
      setVisibleCount(res?.visible_count ?? 0);
      toast.success(`Permessi salvati — ${res?.visible_count ?? 0} clienti visibili`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const isPartial = mode === MODE_PARTIAL;

  return (
    <Card
      className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors"
      data-testid={`agent-access-card-${agent.id}`}
    >
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
              <UserCog className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold truncate">{agent.name}</p>
              <p className="text-zinc-500 text-xs truncate">{agent.email}</p>
              {agent.cod_agente != null && (
                <p className="text-zinc-600 text-[10px] mt-0.5 font-mono">cod_agente: {agent.cod_agente}</p>
              )}
            </div>
          </div>
          <Badge
            className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 shrink-0"
            data-testid={`agent-visible-count-${agent.id}`}
          >
            <Eye className="w-3 h-3 mr-1" />
            {visibleCount} clienti
          </Badge>
        </div>

        {/* Posizione ordini (permesso) */}
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800/40">
          <div className="min-w-0">
            <p className="text-zinc-200 text-xs font-semibold flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-emerald-400" /> Posizione di presa ordini
            </p>
            <p className="text-zinc-500 text-[10px] mt-0.5">
              Se attiva, l&apos;app chiede il permesso GPS una sola volta e registra il punto in cui l&apos;ordine viene creato.
            </p>
          </div>
          <button
            type="button"
            data-testid={`geo-toggle-${agent.id}`}
            onClick={() => onToggleGeo(agent.id, !geoOn)}
            className={`shrink-0 w-14 h-7 rounded-full border transition-colors relative ${
              geoOn ? "bg-emerald-500/30 border-emerald-500/50" : "bg-zinc-700/50 border-zinc-600"
            }`}
            title={geoOn ? "Disattiva registrazione posizione" : "Attiva registrazione posizione"}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full transition-all ${
              geoOn ? "left-7 bg-emerald-400" : "left-0.5 bg-zinc-400"
            }`} />
          </button>
        </div>

        {/* Notifiche push (permesso) */}
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800/40">
          <div className="min-w-0">
            <p className="text-zinc-200 text-xs font-semibold flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5 text-amber-400" /> Notifiche push
            </p>
            <p className="text-zinc-500 text-[10px] mt-0.5">
              Se attiva, l&apos;agente può ricevere notifiche sul telefono (ordini e messaggi dall&apos;admin).
            </p>
          </div>
          <button
            type="button"
            data-testid={`push-toggle-${agent.id}`}
            onClick={() => onTogglePush(agent.id, !pushOn)}
            className={`shrink-0 w-14 h-7 rounded-full border transition-colors relative ${
              pushOn ? "bg-amber-500/30 border-amber-500/50" : "bg-zinc-700/50 border-zinc-600"
            }`}
            title={pushOn ? "Disattiva notifiche" : "Attiva notifiche"}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full transition-all ${
              pushOn ? "left-7 bg-amber-400" : "left-0.5 bg-zinc-400"
            }`} />
          </button>
        </div>

        {/* Toggle Mode */}
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid={`mode-full-${agent.id}`}
            onClick={() => setMode(MODE_FULL)}
            className={`p-3 rounded-lg border text-sm font-medium transition-all flex items-center gap-2 ${
              mode === MODE_FULL
                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600"
            }`}
          >
            <ShieldCheck className="w-4 h-4 shrink-0" />
            Accesso completo
          </button>
          <button
            data-testid={`mode-partial-${agent.id}`}
            onClick={() => setMode(MODE_PARTIAL)}
            className={`p-3 rounded-lg border text-sm font-medium transition-all flex items-center gap-2 ${
              mode === MODE_PARTIAL
                ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600"
            }`}
          >
            <ShieldAlert className="w-4 h-4 shrink-0" />
            Accesso parziale
          </button>
        </div>

        {/* Partial settings */}
        {isPartial && (
          <div className="space-y-4 pt-2 border-t border-zinc-800">
            <div>
              <p className="text-zinc-300 text-xs font-semibold mb-2">
                Codici agente del file clienti
              </p>
              <p className="text-zinc-500 text-[11px] mb-2">
                L&apos;agente vedrà <span className="text-emerald-400">tutti i clienti</span> assegnati a questi codici
              </p>
              <div className="flex flex-wrap gap-1.5">
                {codAgenteStats.length === 0 && (
                  <p className="text-zinc-600 text-xs italic">Nessun codice agente trovato nel file clienti</p>
                )}
                {codAgenteStats.map((s) => {
                  const active = codes.has(s.cod_agente);
                  return (
                    <button
                      key={s.cod_agente}
                      data-testid={`code-chip-${agent.id}-${s.cod_agente}`}
                      onClick={() => toggleCode(s.cod_agente)}
                      className={`px-3 py-1.5 rounded-md text-xs font-mono font-bold border transition-colors ${
                        active
                          ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                          : "bg-zinc-800 border-zinc-700 text-zinc-500 hover:border-zinc-600"
                      }`}
                      title={`${s.clients_count} clienti con cod_agente ${s.cod_agente}`}
                    >
                      {s.cod_agente}
                      <span className="ml-1.5 opacity-60 font-normal">({s.clients_count})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-zinc-300 text-xs font-semibold mb-2">
                Clienti extra
              </p>
              <p className="text-zinc-500 text-[11px] mb-2">
                Aggiungi singoli clienti fuori dai codici agente selezionati. Cerca per nome o codice e tappa sul risultato per aggiungere.
              </p>

              {/* Chip dei clienti già aggiunti */}
              {extras.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`extras-chips-${agent.id}`}>
                  {extras.map((e) => (
                    <span
                      key={e.codice_cli}
                      data-testid={`extra-chip-${agent.id}-${e.codice_cli}`}
                      className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-xs"
                    >
                      <span className="font-mono font-bold text-[10px] opacity-80">{e.codice_cli}</span>
                      <span className="truncate max-w-[180px]">{e.ragione_sociale || "—"}</span>
                      <button
                        data-testid={`remove-extra-${agent.id}-${e.codice_cli}`}
                        onClick={() => removeExtra(e.codice_cli)}
                        className="p-0.5 rounded hover:bg-cyan-500/20 transition-colors"
                        title="Rimuovi cliente"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search box + dropdown */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <Input
                  data-testid={`extras-search-${agent.id}`}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                  placeholder="Cerca cliente per nome o codice (es. EUROSIGMA, AA20)"
                  className="pl-9 bg-zinc-800 border-zinc-700 text-white text-xs"
                />
                {dropdownOpen && (results.length > 0 || searching) && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl shadow-black/60"
                    data-testid={`extras-results-${agent.id}`}
                  >
                    {searching && (
                      <div className="px-3 py-2 text-xs text-zinc-500">Cerco...</div>
                    )}
                    {!searching && results.map((r) => {
                      const already = extras.some((e) => e.codice_cli === r.codice_cli);
                      return (
                        <button
                          key={r.codice_cli}
                          data-testid={`extras-result-${agent.id}-${r.codice_cli}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !already && addExtra(r)}
                          disabled={already}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-zinc-800 last:border-0 transition-colors ${
                            already
                              ? "opacity-40 cursor-not-allowed"
                              : "hover:bg-cyan-500/10 text-zinc-200"
                          }`}
                        >
                          <span className="font-mono font-bold text-[10px] text-cyan-400 shrink-0 w-16">{r.codice_cli}</span>
                          <span className="flex-1 min-w-0 truncate">{r.ragione_sociale}</span>
                          {r.cod_agente != null && (
                            <span className="text-[9px] text-zinc-500 font-mono shrink-0">ag.{r.cod_agente}</span>
                          )}
                          {!already && <Plus className="w-3 h-3 text-cyan-400 shrink-0" />}
                          {already && <span className="text-[9px] text-zinc-500">già aggiunto</span>}
                        </button>
                      );
                    })}
                    {!searching && results.length === 0 && query.trim() && (
                      <div className="px-3 py-2 text-xs text-zinc-500 italic">Nessun cliente trovato</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Giri whitelist */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-zinc-300 text-xs font-semibold">
                  Giri visibili
                </p>
                {allowedGiri.size > 0 && (
                  <span className="text-[10px] text-violet-400" data-testid={`giri-count-${agent.id}`}>
                    {allowedGiri.size} su {giriOptions.length}
                  </span>
                )}
              </div>
              <p className="text-zinc-500 text-[11px] mb-2">
                Se selezioni almeno un giro, l&apos;agente vedrà <span className="text-violet-300">solo quei giri</span>. Lascia vuoto per mostrare i giri secondo i clienti visibili.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {giriOptions.length === 0 && (
                  <p className="text-zinc-600 text-xs italic">Nessun giro presente nel sistema</p>
                )}
                {giriOptions.map((g) => {
                  const key = `${g.cod_agente}|${g.cod_giorno}`;
                  const active = allowedGiri.has(key);
                  return (
                    <button
                      key={key}
                      data-testid={`giro-chip-${agent.id}-${g.cod_agente}-${g.cod_giorno}`}
                      onClick={() => toggleGiro(key)}
                      className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-bold border transition-colors ${
                        active
                          ? "bg-violet-500/15 border-violet-500/40 text-violet-300"
                          : "bg-zinc-800 border-zinc-700 text-zinc-500 hover:border-zinc-600"
                      }`}
                      title={`${g.giorno_label || ""} — ${g.fermate} fermate`}
                    >
                      {g.cod_agente}-{g.cod_giorno}
                      <span className="ml-1.5 opacity-60 font-normal">({g.fermate})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Save button */}
        <div className="flex justify-end pt-1">
          <Button
            data-testid={`save-access-${agent.id}`}
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminAgentsAccess() {
  const { authAxios } = useAuth();
  const [agents, setAgents] = useState([]);
  const [codAgenteStats, setCodAgenteStats] = useState([]);
  const [giriOptions, setGiriOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [accessRes, giriRes] = await Promise.all([
        authAxios.get("/users/agents/access"),
        authAxios.get("/admin/giri-lookup"),
      ]);
      setAgents(accessRes.data.agents || []);
      setCodAgenteStats(accessRes.data.cod_agente_stats || []);
      setGiriOptions(giriRes.data.items || []);
    } catch {
      toast.error("Errore nel caricamento permessi agenti");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const handleSave = useCallback(async (userId, payload) => {
    const res = await authAxios.put(`/users/${userId}/client-access`, payload);
    setAgents((prev) =>
      prev.map((a) =>
        a.id === userId
          ? { ...a, client_access: res.data.client_access, visible_count: res.data.visible_count }
          : a
      )
    );
    return res.data;
  }, [authAxios]);

  const handleToggleGeo = useCallback(async (userId, enabled) => {
    try {
      await authAxios.put(`/users/${userId}/geo-tracking`, { enabled });
      setAgents((prev) => prev.map((a) => (a.id === userId ? { ...a, geo_tracking: enabled } : a)));
      toast.success(enabled ? "Posizione ordini attivata" : "Posizione ordini disattivata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    }
  }, [authAxios]);

  const handleTogglePush = useCallback(async (userId, enabled) => {
    try {
      await authAxios.put(`/users/${userId}/push-enabled`, { enabled });
      setAgents((prev) => prev.map((a) => (a.id === userId ? { ...a, push_enabled: enabled } : a)));
      toast.success(enabled ? "Notifiche attivate" : "Notifiche disattivate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = agents.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (a.name || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="agents-access-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <PageHeader title="Permessi Agenti" />
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <UserCog className="w-7 h-7 text-cyan-400" />
            Permessi Agenti
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Limita quali clienti ogni agente può vedere. <span className="text-emerald-400">Accesso completo</span> = vede tutto.{" "}
            <span className="text-amber-400">Accesso parziale</span> = solo i codici agente selezionati + clienti extra.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="agents-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca agente per nome o email..."
            className="pl-10 bg-[#18181b] border-white/10 text-white"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-48 rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-10 text-center">
              <UserCog className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">
                {agents.length === 0
                  ? "Nessun utente agente trovato"
                  : "Nessun agente corrisponde alla ricerca"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => (
              <AgentCard
                key={a.id}
                agent={a}
                codAgenteStats={codAgenteStats}
                giriOptions={giriOptions}
                authAxios={authAxios}
                onSave={handleSave}
                onToggleGeo={handleToggleGeo}
                onTogglePush={handleTogglePush}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
